import { LinkBox, LinkOverlay, VStack } from "@chakra-ui/react"
import { useKatex } from "@pathwright/utils/src/katex/useKatex"
import Link from "@pathwright/ux/src/components/link/Link"
import styled from "styled-components"
import { DISCUSSION_TYPE_FEEDBACK } from "../discussion/constants"
import DiscussionDockPanel from "../discussion/panel/DiscussionDockPanel"
import { getDiscussionUrl, getResponseUrl } from "../utils/urls"
import ResourceThumbnail from "./ResourceThumbnail"

const StyledDiscussion = styled.div`
  padding: 10px 20px;
  ${(props) => props.$hasThumbnail && "padding-left: 30px;"}

  &:not(:last-child) {
    color: #888;
    font-size: 13px;
  }

  > div > p:first-child {
    margin-top: 0 !important;
  }

  > div > p:last-child {
    margin-bottom: 0 !important;
  }
`

const StyledResponse = styled.div`
  overflow: hidden;
  padding: 10px 20px;
  border-radius: 0 0 8px 8px;
  background-color: rgba(0, 0, 0, 0.05);
  width: 100%;

  img {
    max-width: 100%;
  }

  > div > p:first-child {
    margin-top: 0 !important;
  }

  > div > p:last-child {
    margin-bottom: 0 !important;
  }
`

const ellipsify = (str, limit) => {
  return str.length > limit ? str.slice(0, limit) + "..." : str
}

const DiscussionActivity = ({
  resource,
  group,
  step,
  discussion,
  discussion_response,
  isCourseActivity,
  isStepActivity
}) => {
  const [discussionPanel, setDiscussionPanel] = React.useState(null)
  const containerRef = React.useRef(null)

  useKatex(containerRef, { contentKey: discussion_response })

  if (!resource || !group || !discussion) {
    return null
  }
  let toDiscussion
  if (discussion_response) {
    toDiscussion = getResponseUrl({
      resourceSlug: resource.slug,
      groupId: group.id,
      stepId: step ? step.source_id : null,
      discussionId: discussion.id,
      responseId: discussion_response.id
    })
  } else {
    toDiscussion = getDiscussionUrl({
      resourceSlug: resource.slug,
      groupId: group.id,
      stepId: step ? step.source_id : null,
      discussionId: discussion.id
    })
  }

  if (discussion.type === DISCUSSION_TYPE_FEEDBACK) {
    // Oddities have been detected where the step does not exist on the item.
    if (step) {
      const urlParams = new URLSearchParams({
        cohort_id: group.id,
        step_id: step.id,
        ui_selected: step.id,
        ui_selected_parent: group.id,
        filter: "all",
        ui_panel: "discuss"
      })

      if (discussion_response) {
        urlParams.set("ui_panel_selected", discussion_response.id)
      }

      if (step.completion && step.completion.user) {
        urlParams.set("user_id", step.completion.user.id)
      }

      toDiscussion = `/inbox/?${urlParams.toString()}`
    } else {
      console.warn("Discussion activity with missing step", {
        resource,
        group,
        step,
        discussion,
        discussion_response
      })
    }
  }

  const handleOpenDiscussion = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      // Allow opening in new tab/window
      return
    }

    e.preventDefault()

    const panelOptions = {
      discussionId: discussion.id,
      context: step
        ? {
            step_id: step.source_id,
            resource_id: resource.id,
            cohort_id: group.id
          }
        : {
            resource_id: resource.id,
            cohort_id: group.id
          },
      onClose: () => setDiscussionPanel(null)
    }

    setDiscussionPanel({
      ...panelOptions,
      responseId: discussion_response?.id || null,
      // Resets the panel with options excluding the responseId.
      onViewAllResponses: () => {
        setDiscussionPanel(panelOptions)
      }
    })
  }

  const linkProps =
    discussion.type === DISCUSSION_TYPE_FEEDBACK
      ? {
          to: toDiscussion
        }
      : {
          href: toDiscussion,
          onClick: handleOpenDiscussion
        }

  // Is the activity rendered outside of the original context where the discussion was created?
  const outsideOfOriginContext = !(isCourseActivity || isStepActivity)

  return (
    <>
      <LinkBox
        ref={containerRef}
        position="relative"
        borderRadius="8px"
        fontSize="16px"
        color="black"
        backgroundColor="rgba(255, 255, 255, 0.9)"
        _hover={{
          backgroundColor: "white"
        }}
      >
        <VStack w="full" spacing={0} alignItems="stretch">
          {outsideOfOriginContext && <ResourceThumbnail resource={resource} />}
          {!discussion_response?.id && (
            <Link
              as={LinkOverlay}
              {...linkProps}
              color="inherit"
              whiteSpace="normal"
              w="full"
              textDecoration="none"
            >
              <StyledDiscussion $hasThumbnail={outsideOfOriginContext}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: ellipsify(discussion.title, 100)
                  }}
                />
              </StyledDiscussion>
            </Link>
          )}
          {!!discussion_response?.id && (
            <StyledDiscussion $hasThumbnail={outsideOfOriginContext}>
              {discussion_response.parent_response ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: ellipsify(
                      discussion_response.parent_response.body,
                      100
                    )
                  }}
                />
              ) : (
                discussion.title
              )}
            </StyledDiscussion>
          )}
          {!!discussion_response?.id && (
            <Link
              as={LinkOverlay}
              {...linkProps}
              color="gray.600"
              whiteSpace="normal"
              textDecoration="none"
              w="full"
            >
              <StyledResponse>
                <div
                  dangerouslySetInnerHTML={{
                    __html: ellipsify(discussion_response.body, 100)
                  }}
                />
              </StyledResponse>
            </Link>
          )}
        </VStack>
      </LinkBox>
      {discussionPanel && <DiscussionDockPanel {...discussionPanel} />}
    </>
  )
}

DiscussionActivity.displayName = "DiscussionActivity"

export default DiscussionActivity
